import { ActionIcon, Button, Stack, Text } from '@mantine/core'
import { IconLink, IconPencil, IconX } from '@tabler/icons-react'
import { Link } from 'react-router-dom'
import { useOutgoingFaxLinks, useUnlinkOutgoingFax } from '@/hooks/useFaxTasks'
import type { EditingFaxLink } from './LinkFaxToTaskModal'

/** The Care Coordination tasks this sent fax has been recorded against (PD-1606). */
export function LinkedTasksPanel({
  messageId,
  onLink,
  onEdit,
}: {
  messageId: string
  onLink: () => void
  /** Re-point this link at another task, change its note, or both. */
  onEdit: (link: EditingFaxLink) => void
}) {
  const { data } = useOutgoingFaxLinks(messageId)
  const unlink = useUnlinkOutgoingFax()
  const tasks = data?.tasks ?? []

  return (
    <div>
      <Text size="xs" c="dimmed" className="mb-1">
        Care Coordination
      </Text>
      {tasks.length === 0 ? (
        <Text size="sm" c="dimmed">
          Not linked to a task.
        </Text>
      ) : (
        <Stack gap="xs">
          {tasks.map((task) => (
            <div key={task.itemId} className="flex items-start justify-between gap-2">
              {/* min-w-0 lets a long task name wrap inside the row instead of
                  widening it and shoving the controls out of the column. */}
              <Link
                to={`/appointments?tab=care-queue&board=${task.boardId}&item=${task.itemId}`}
                className="hover:text-primary min-w-0 flex-1 text-sm underline-offset-2 hover:underline"
              >
                {/* A completed task stays listed — the fax record outlives the
                    work — but reads as finished rather than outstanding. */}
                <span className={task.done ? 'text-gray-500 line-through' : ''}>{task.name}</span>
                <Text size="xs" c="dimmed">
                  {task.boardName}
                </Text>
              </Link>
              {/* One group, not two loose children: with three flex children
                  `justify-between` spreads the gaps between each pair, so the
                  pencil drifted with the length of the task name while only the
                  cross stayed pinned right. */}
              <div className="flex shrink-0 items-center gap-0.5">
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  aria-label={`Edit link to ${task.name}`}
                  onClick={() =>
                    onEdit({ itemId: task.itemId, boardId: task.boardId, note: task.note })
                  }
                >
                  <IconPencil size={14} />
                </ActionIcon>
                {/* A cross, not a broken-chain glyph: at 14px the chain reads as
                    decoration, and "remove this row" is what the control does. */}
                <ActionIcon
                  variant="subtle"
                  color="gray"
                  size="sm"
                  aria-label={`Unlink ${task.name}`}
                  loading={unlink.isPending}
                  onClick={() => unlink.mutate({ itemId: task.itemId, messageId })}
                >
                  <IconX size={14} />
                </ActionIcon>
              </div>
            </div>
          ))}
        </Stack>
      )}
      {/* Full width and neutral, so it shares the column's edges with Delete fax
          and stays the quieter of the two — the destructive button keeps the
          only saturated fill in this sidebar. */}
      <Button
        variant="default"
        fullWidth
        className="mt-3"
        leftSection={<IconLink size={16} />}
        onClick={onLink}
      >
        Link to task
      </Button>
    </div>
  )
}
